import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Icon, Popup, Grid, Header } from 'semantic-ui-react';
import { Message } from 'components';
import { ExposureChart, ExposureTable } from '../components';
import RingChart from '../../../../components/RingChart/RingChart';
import assignColors from '../../../../utils/assignColors';
import {
  getLabels,
  translateLabels,
  translateText,
} from '../../../../utils/helpers';
import { colors } from '../../../../utils/colors';

@connect((state) => ({
  french: state.Storage.language === 'fr',
  partnerName: state.Storage['partner-name'],
}))
export default class Region extends Component {
  hasNegative = (data) => data.filter((item) => item < 0).length !== 0;

  renderSubheader = (type) => {
    const { french } = this.props;
    return (
      <div className="sub header">
        {type === 'domicile'
          ? french
            ? 'Par région de domicile'
            : 'By Region of Domicile'
          : french
          ? 'Aux marchés boursiers régionaux'
          : 'To Regional Stock Markets'}
        &nbsp;
        <Popup
          trigger={
            <Icon
              name="question circle outline"
              style={{
                fontSize: '15px',
                verticalAlign: 'initial',
                color: 'dimgrey',
              }}
            />
          }
          position="top center"
          wide
          content={
            type === 'domicile'
              ? french
                ? 'Comprends les actions et les obligations dans votre portefeuille'
                : 'Including stocks and bonds in your portfolio'
              : french
              ? 'Une analyse statistique nous permet d’estimer l’exposition de votre portefeuille aux fluctuations des différents marchés boursiers régionaux. Si votre portefeuille est majoritairement composé d’obligations, les chiffres peuvent être très différents de ceux figurant dans la répartition des avoirs.'
              : 'Using statistical analysis, we estimate the exposure of your portfolio to fluctuations in different regional stock markets. If you have a predominantly bonds portfolio, the numbers may be very different from those under holdings breakdown.'
          }
        />
      </div>
    );
  };

  render() {
    const { data, french, partnerName } = this.props;

    const assetData = {};
    const riskData = {};

    if (data.domicile && typeof data.domicile === 'object') {
      const tableLabels = [
        ...Object.keys(data.domicile),
        'Port. Specific Risk',
      ];
      tableLabels.forEach((label) => {
        assetData[label] = label in data.domicile ? data.domicile[label] : 0;
      });

      if (data.riskExp.C && typeof data.riskExp.C === 'object') {
        tableLabels.forEach((label) => {
          riskData[label] = label in data.riskExp.C ? data.riskExp.C[label] : 0;
        });
      }
    }

    const chartColors = assignColors(
      [...Object.keys(data.domicile || {}), 'Port. Specific Risk'],
      partnerName
    );

    return (
      <Container>
        <Header dividing size="large">
          {french
            ? 'Analyse des avoirs et de l’exposition au risque'
            : 'Holdings and Risk Exposure Analysis'}
          <Header.Subheader>
            <div className="subheader">
              {french ? 'Par ' : 'By '}
              <div className="pill">{french ? 'région' : 'Region'}</div>
            </div>
          </Header.Subheader>
        </Header>

        <Grid>
          <Grid.Row columns={2}>
            <Grid.Column>
              <Header
                size="medium"
                content={
                  french ? 'Répartition des avoirs' : 'Holdings Breakdown'
                }
                subheader={this.renderSubheader('domicile')}
                style={{ margin: 0 }}
              />
              {typeof data.domicile === 'object' ? (
                this.hasNegative(Object.values(data.domicile)) ? (
                  <ExposureChart
                    title=""
                    labels={getLabels(Object.keys(assetData), true)}
                    data={Object.values(assetData)}
                    frontierMarkets
                  />
                ) : (
                  <div className="chart">
                    <RingChart
                      labels={translateLabels(
                        getLabels(Object.keys(assetData), true),
                        french
                      )}
                      values={Object.values(assetData)}
                      maxHeight={350}
                      maxWidth={350}
                      colors={chartColors}
                    />
                  </div>
                )
              ) : (
                <Message visible warning>
                  {translateText(data.domicile, french)}
                </Message>
              )}
            </Grid.Column>
            <Grid.Column>
              <Header
                size="medium"
                content={french ? 'Exposition au risque' : 'Risk Exposure'}
                subheader={this.renderSubheader('market')}
                style={{ margin: 0 }}
              />
              {typeof data.riskExp.C === 'object' ? (
                this.hasNegative(Object.values(data.riskExp.C)) ? (
                  <ExposureChart
                    title=""
                    labels={Object.keys(riskData)}
                    data={Object.values(riskData)}
                    frontierMarkets
                  />
                ) : (
                  <div className="chart">
                    <RingChart
                      labels={translateLabels(
                        getLabels(Object.keys(riskData), true),
                        french
                      )}
                      values={Object.values(riskData)}
                      maxHeight={350}
                      maxWidth={350}
                      colors={chartColors}
                    />
                  </div>
                )
              ) : (
                <Message visible warning>
                  {translateText(data.riskExp.C, french)}
                </Message>
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
        {typeof data.riskExp.C === 'string' &&
        typeof data.domicile === 'string' ? null : (
          <ExposureTable
            headers={[
              french ? 'Région' : 'Region',
              <div>
                {french ? 'Répartition des avoirs' : 'Holdings Breakdown'}
                <Popup
                  trigger={
                    <Icon
                      name="question circle outline"
                      style={{
                        fontSize: '15px',
                        verticalAlign: 'initial',
                        color: 'dimgrey',
                      }}
                    />
                  }
                  position="top center"
                  wide
                  content={
                    french
                      ? 'Comprends les actions et les obligations dans votre portefeuille.'
                      : 'Including stocks and bonds in your portfolio'
                  }
                  frontierMarkets
                />
              </div>,
              <div>
                {french ? 'Exposition au risque' : 'Risk Exposure'}
                <Popup
                  trigger={
                    <Icon
                      name="question circle outline"
                      style={{
                        fontSize: '15px',
                        verticalAlign: 'initial',
                        color: 'dimgrey',
                      }}
                    />
                  }
                  position="top center"
                  wide
                  content={
                    french
                      ? 'Une analyse statistique nous permet d’estimer l’exposition de votre portefeuille aux fluctuations des différents marchés boursiers régionaux. Si votre portefeuille est majoritairement composé d’obligations, les chiffres peuvent être très différents de ceux figurant dans la répartition des avoirs.'
                      : 'Using statistical analysis, we estimate the exposure of your portfolio to fluctuations in different regional stock markets. If you have a predominantly bonds portfolio, the numbers may be very different from those under holdings breakdown.'
                  }
                />
              </div>,
            ]}
            totals={[
              french ? 'Total' : 'Total',
              <div>100%</div>,
              <div>100%</div>,
            ]}
            data={[...Object.keys(data.domicile), 'Port. Specific Risk'].reduce(
              (total, key) => {
                total.push({
                  region: key === 'Other' ? 'Frontier Markets' : key,
                  holdingsBreakdown: data.domicile[key],
                  riskExposure: data.riskExp ? data.riskExp.C[key] : null,
                });

                return total;
              },
              []
            )}
            fullWidth
          />
        )}
      </Container>
    );
  }
}

Region.propTypes = {
  data: PropTypes.object.isRequired,
  french: PropTypes.bool,
  partnerName: PropTypes.string,
};

Region.defaultProps = { french: false, partnerName: '' };

const Container = styled.div`
  padding: 2rem 2rem 1rem 2rem;

  .subheader {
    margin-top: 5px;
    font-size: 20px;
    font-weight: bold;
    display: flex;
    flex-direction: row;
    align-items: center;
    width: fit-content;
    column-gap: 5px;

    .pill {
      background-color: ${colors.lightGrey};
      border-radius: 15px;
      padding: 5px 10px 5px 10px;
      margin: auto;
    }
  }

  .chart {
    padding: 0rem 7rem 2rem 7rem;
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
  }
`;
